<template>
  <button
    :class="[
      'font-normal flex flex-col items-center justify-center cursor-pointer',
      'focus:outline-none border',
      classes,
      { 'pointer-events-none opacity-40': disabled },
    ]"
    :disabled="disabled"
    v-on="$listeners"
  >
    <span
      class="flex flex-row items-center justify-center"
      :class="{ 'h-0 overflow-hidden': !busy }"
    >
      <slot name="busy">
      </slot>
    </span>
    <span
      class="flex flex-row items-center justify-center"
      :class="{ 'h-0 overflow-hidden': busy }"
    >
      <slot />
    </span>
  </button>
</template>

<script>
const _palettes = [
  'primary-200',
  'neutral-200',
  'danger-400',
  'neutral-border',
];
const _looks = [
  'default',
  'outline',
];
const _sizes = [
  'small',
  'medium',
  'normal',
];
const _schemes = {
  'primary-200': {
    default: {
      blur: {
        bg: 'primary-200', // bg-primary-200
        border: 'primary-200', // border-primary-200
        text: 'white', // text-white
      },
      hover: {
        bg: 'primary-300', // hover:bg-primary-300
        border: 'primary-300', // hover:border-primary-300
        text: 'white', // hover:text-white
      },
    },
  },
  'neutral-200': {
    default: {
      blur: {
        bg: 'neutral-200', // bg-neutral-200
        border: 'neutral-200', // border-neutral-200
        text: 'primary-200', // primary-200
      },
      hover: {
        bg: 'neutral-250', // hover:bg-neutral-250
        border: 'neutral-250', // hover:border-neutral-250
        text: 'primary-200', // hover:text-primary-200
      },
    },
  },
  'neutral-border': {
    default: {
      blur: {
        bg: 'transparent', // bg-transparent
        border: 'neutral-700', // border-neutral-700
        text: 'neutral-700', // text-neutral-7000
      },
      hover: {
        bg: 'transparent', // hover:bg-transparent
        border: 'primary-50', // hover:border-primary-50
        text: 'primary-50', // hover:text-primary-50
      },
    },
  },
  'danger-400': {
    default: {
      blur: {
        bg: 'danger-100', // bg-danger-100
        border: 'danger-100', // border-danger-100
        text: 'danger-200', // text-danger-200
      },
      hover: {
        bg: 'danger-150', // hover:bg-danger-150
        border: 'neutral-250', // hover:border-danger-150
        text: 'primary-200', // hover:text-primary-200
      },
    },
  },
};

export default {
  name: 'Button',
  props: {
    palette: {
      type: String,
      default: 'primary-200',
    },
    look: {
      type: String,
      default: 'default',
    },
    size: {
      type: String,
      default: 'normal',
    },
    shadow: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    _palette() {
      if (_palettes.includes(this.palette)) {
        return this.palette;
      }
      return 'primary-200';
    },
    _look() {
      if (_looks.includes(this.look)) {
        return this.look;
      }
      return 'default';
    },
    _size() {
      if (_sizes.includes(this.size)) {
        return this.size;
      }
      return 'normal';
    },
    _scheme() {
      return _schemes[this._palette]?.[this._look];
    },
    classes() {
      const data = [
        `bg-${this._scheme.blur.bg}`,
        `border-${this._scheme.blur.border}`,
        `text-${this._scheme.blur.text}`,
        `hover:bg-${this._scheme.hover.bg}`,
        `hover:border-${this._scheme.hover.border}`,
        `hover:text-${this._scheme.hover.text}`,
      ];

      switch (this.size) {
        case 'small':
          data.push('py-1 px-3');
          break;
        case 'medium':
          data.push('py-3 px-5 leading-none');
          break;
        case 'normal':
          data.push('py-3 px-10 md:px-5 text-base');
          break;
        default:
      }

      if (this.shadow) {
        const shadow = {
          blur: this._scheme.blur.shadow || 'none',
          hover: this._scheme.hover.shadow || 'none',
        };

        data.push(`shadow-${shadow.blur}`);
        data.push(`hover:shadow-${shadow.hover}`);
      }

      if (this.rounded) {
        data.push('rounded-10px');
      }

      if (this._scheme.disabled) {
        data.push(`disabled:bg-${this._scheme.disabled.bg}`);
        data.push(`disabled:border-${this._scheme.disabled.border}`);
        data.push(`disabled:text-${this._scheme.disabled.text}`);
      }

      return data.join(' ');
    },
  },
};
</script>
